import React from 'react';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Language } from '@/utils/i18n';

interface LanguageSwitcherProps {
  variant?: 'dropdown' | 'buttons' | 'compact';
  showLabel?: boolean;
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'dropdown',
  showLabel = true,
  className = ''
}) => {
  const { language, setLanguage, t, isRTL } = useTranslation();

  const languages = [
    { code: 'en' as Language, name: 'English', flag: '🇺🇸' },
    { code: 'hi' as Language, name: 'हिंदी', flag: '🇮🇳' }
  ];

  if (variant === 'buttons') {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {showLabel && (
          <span className="text-sm font-medium text-gray-700">
            {t('common.language')}:
          </span>
        )}
        <div className="flex bg-gray-100 rounded-lg p-1">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                language === lang.code
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="mr-2">{lang.flag}</span>
              {lang.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <button
        onClick={() => setLanguage(language === 'en' ? 'hi' : 'en')}
        className={`flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors ${className}`}
        title={language === 'en' ? 'Switch to Hindi' : 'Switch to English'}
      >
        <span className="text-lg">
          {language === 'en' ? '🇮🇳' : '🇺🇸'}
        </span>
      </button>
    );
  }

  // Default dropdown variant
  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center space-x-2">
        <Globe className="h-4 w-4 text-gray-500" />
        {showLabel && (
          <span className="text-sm font-medium text-gray-700">
            {t('common.language')}:
          </span>
        )}
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value as Language)}
          data-testid="language-switcher"
          className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {languages.map((lang) => (
            <option key={lang.code} value={lang.code} data-testid={`${lang.code}-option`}>
              {lang.flag} {lang.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Mobile-friendly language switcher
export const MobileLanguageSwitcher: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { language, setLanguage, t } = useTranslation();

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      <div className="p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">
          {t('common.language')}
        </h3>
        <div className="space-y-2">
          {[
            { code: 'en' as Language, name: 'English', flag: '🇺🇸' },
            { code: 'hi' as Language, name: 'हिंदी', flag: '🇮🇳' }
          ].map((lang) => (
            <button
              key={lang.code}
              onClick={() => setLanguage(lang.code)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                language === lang.code
                  ? 'bg-blue-50 text-blue-600 border border-blue-200'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center">
                <span className="mr-3 text-lg">{lang.flag}</span>
                <span>{lang.name}</span>
              </div>
              {language === lang.code && (
                <Check className="h-4 w-4 text-blue-600" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Language indicator component
export const LanguageIndicator: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { language } = useTranslation();
  
  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      <span className="text-sm">
        {language === 'en' ? '🇺🇸' : '🇮🇳'}
      </span>
      <span className="text-xs text-gray-500">
        {language === 'en' ? 'EN' : 'HI'}
      </span>
    </div>
  );
};

export default LanguageSwitcher;
